import {
  contract, core, debt as debtNs, forecast, formatDay, parseDay, plan as planNs, schedule, withTimeframe,
  type Checkpoint, type Day, type Decimal, type GoalProgress, type Plan, type PlanForecast, type Series, type Transaction, type Wire,
} from '@map-my-money/engine';
import { ADDED_PREFIX, type CheckInFlow } from './flows.ts';

export type WirePlan = Wire.Plan;

export interface WaypointInput {
  readonly id: string;
  readonly date: string;
  readonly balances: Wire.Balances;
  readonly createdAt?: string;
  readonly source?: string;
  /** Each debt's balance as typed (decision 1): from its day the debt counts from it (R23). */
  readonly debts?: Readonly<Record<string, string>>;
  /** Fields saved from an untouched pre-fill (`cash`, `investments`, `debt:<id>`): they anchor nothing (decision 2). */
  readonly estimates?: readonly string[];
  /** What happened to each payment the map expected since the check-in before (R27); absent or null: not reviewed. */
  readonly flows?: readonly CheckInFlow[] | null;
  /** The first day step 1 covered; null: not reviewed. */
  readonly reviewedFrom?: string | null;
}

/** Everything a view is a pure function of. */
export interface ViewInput {
  readonly plan: WirePlan;
  readonly waypoints: readonly WaypointInput[];
  /** "Today" as YYYY-MM-DD; nothing in this package reads a clock. */
  readonly asOf: string;
}

const decoded = new WeakMap<WirePlan, Plan>();

/** contract.decodePlan, memoised per wire object (the web app re-renders with the same plan object). */
export function decode(wire: WirePlan): Plan {
  let plan = decoded.get(wire);
  if (!plan) {
    plan = contract.decodePlan(wire);
    decoded.set(wire, plan);
  }
  return plan;
}

// Same-day waypoints: the one recorded last is the latest, so a correction wins.
const waypointOrder = (a: WaypointInput, b: WaypointInput): number =>
  a.date !== b.date ? (a.date < b.date ? -1 : 1) : (a.createdAt ?? '') !== (b.createdAt ?? '') ? ((a.createdAt ?? '') < (b.createdAt ?? '') ? -1 : 1) : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;

/** Waypoints oldest first, in the one order every surface uses. */
export const orderedWaypoints = (waypoints: readonly WaypointInput[]): WaypointInput[] => [...waypoints].sort(waypointOrder);

export const latestWaypoint = (waypoints: readonly WaypointInput[]): WaypointInput | null => orderedWaypoints(waypoints).at(-1) ?? null;

/** Every waypoint as a forecast checkpoint, oldest first. */
export function checkpointsFrom(plan: Plan, waypoints: readonly WaypointInput[]): Checkpoint[] {
  return orderedWaypoints(waypoints).map((w) => {
    const balances = contract.decodeBalances(w.balances);
    return planNs.resolveCheckpoint({ id: w.id, date: parseDay(w.date), confirmedBalances: balances, forecastedBalances: balances }, plan);
  });
}

const typed = (w: WaypointInput, field: 'cash' | 'investments'): boolean => !(w.estimates ?? []).includes(field);

/** A check-in re-anchors now when the person typed at least one of its figures (R25). */
export const anchorsNow = (w: WaypointInput): boolean =>
  typed(w, 'cash') || typed(w, 'investments') || Object.keys(w.debts ?? {}).some((id) => !(w.estimates ?? []).includes(`debt:${id}`));

/** The check-ins that count on `asOf`: none dated after it (R25), oldest first. */
export const checkInsOn = (waypoints: readonly WaypointInput[], asOf: string): WaypointInput[] => orderedWaypoints(waypoints).filter((w) => w.date <= asOf);

/** The latest check-in that typed something: what now starts from, or null when now is the setup estimate. */
export const anchorOf = (waypoints: readonly WaypointInput[], asOf: string): WaypointInput | null =>
  checkInsOn(waypoints, asOf).filter(anchorsNow).at(-1) ?? null;

export interface Forecasted {
  readonly plan: Plan;
  readonly checkpoints: readonly Checkpoint[];
  /** The check-in now starts from (the latest that typed something), or null for the setup estimate. */
  readonly checkpoint: Checkpoint | null;
  readonly forecast: PlanForecast;
  /** Every goal on the line (engine `goals.timelineGoals`), the one definition of progress and dates. */
  readonly goals: readonly GoalProgress[];
  readonly today: Day;
  readonly asOf: string;
  readonly years: number;
}

/** The check-ins now's cash and investments count from on `asOf`: the latest that typed each (R25). */
export function checkInAnchors(plan: Plan, waypoints: readonly WaypointInput[], asOf: Day | string): { checkpoint: Checkpoint | null; investmentCheckpoint: Checkpoint | null } {
  const counted = checkInsOn(waypoints, typeof asOf === 'string' ? asOf : formatDay(asOf));
  const latestTyping = (field: 'cash' | 'investments') => counted.filter((w) => typed(w, field)).at(-1);
  const cash = latestTyping('cash');
  const invested = latestTyping('investments');
  const checkpoints = checkpointsFrom(plan, [cash, invested].filter((w): w is WaypointInput => w !== undefined));
  const byId = (w: WaypointInput | undefined) => (w === undefined ? null : checkpoints.find((c) => c.id === w.id)!);
  return { checkpoint: byId(cash), investmentCheckpoint: byId(invested) };
}

/**
 * Decision 1, R23: each debt counts from the latest check-in on or before `asOf` that typed its balance
 * (an estimate anchors nothing), unless the balance on the plan itself was typed later.
 */
export function withTypedDebts(plan: Plan, waypoints: readonly WaypointInput[], asOf: string): Plan {
  const counted = checkInsOn(waypoints, asOf);
  if (!counted.some((w) => w.debts !== undefined)) return plan;
  const today = parseDay(asOf);
  const transactions = plan.transactions.map((t) => {
    if (t.type !== 'debt') return t;
    const w = counted.filter((x) => x.debts?.[t.id] !== undefined && !(x.estimates ?? []).includes(`debt:${t.id}`)).at(-1);
    if (!w) return t;
    const date = parseDay(w.date);
    if (t.debtEntryMode === 'currentBalance' && t.debtBalanceDate !== null && t.debtBalanceDate > date) return t;
    const balance = core.abs(contract.decodeDecimal(w.debts![t.id]!));
    // Confirming the map's own figure (to the penny) moves nothing, not even its rounding.
    if (core.compare(core.round2(core.abs(debtNs.remainingBalance(t, date, today))), balance) === 0) return t;
    return { ...t, debtCheckIn: { date, balance } };
  });
  return { ...plan, transactions };
}

const isWithdrawal = (t: Transaction, day: Day): boolean => t.type === 'investment' && core.lt(schedule.amountOn(t, day), core.ZERO);

/**
 * R27: the payments check-ins recorded pay what was recorded, and their one-offs are on the map on
 * their day. Of several check-ins on one day only the one recorded last carries flows; a payment
 * named by two check-ins takes the later's word; a row of an item no longer on the map counts nothing.
 */
export function withCheckInFlows(plan: Plan, waypoints: readonly WaypointInput[], asOf: string): Plan {
  const counted = checkInsOn(waypoints, asOf).filter((w) => (w.flows?.length ?? 0) > 0 || w.reviewedFrom);
  if (counted.length === 0) return plan;
  const lastOfDay = new Map(checkInsOn(waypoints, asOf).map((w) => [w.date, w.id]));
  const actuals = new Map<string, Map<Day, Decimal>>();
  const added: Transaction[] = [];
  for (const w of counted) {
    if (lastOfDay.get(w.date) !== w.id) continue;
    for (const row of w.flows ?? []) {
      if (row.status === 'added') {
        added.push(contract.decodeTransaction({ id: `${ADDED_PREFIX}${w.id}:${row.seq}`, label: row.label, type: row.kind as 'income' | 'expense', amount: row.actual, frequency: 'oneTime', startDate: row.date }));
      } else if (row.item !== null) {
        const days = actuals.get(row.item) ?? new Map<Day, Decimal>();
        days.set(parseDay(row.date), contract.decodeDecimal(row.actual));
        actuals.set(row.item, days);
      }
    }
  }
  const transactions = plan.transactions.map((t) => {
    const days = actuals.get(t.id);
    if (!days) return t;
    return { ...t, actuals: new Map([...days].map(([day, paid]) => [day, isWithdrawal(t, day) ? core.negate(paid) : paid] as const)) };
  });
  return { ...plan, transactions: [...transactions, ...added] };
}

/** The map as the check-ins on or before `asOf` leave it: typed debts re-anchored (R23) and recorded payments (R27). */
export const planAsOf = (plan: Plan, waypoints: readonly WaypointInput[], asOf: string): Plan =>
  withCheckInFlows(withTypedDebts(plan, waypoints, asOf), waypoints, asOf);

export function runForecast(input: ViewInput, years?: number): Forecasted {
  const base = planAsOf(decode(input.plan), input.waypoints, input.asOf);
  const horizon = years ?? base.configuration.timeframeYears;
  const plan = horizon === base.configuration.timeframeYears ? base : withTimeframe(base, horizon);
  const counted = checkInsOn(input.waypoints, input.asOf);
  const checkpoints = checkpointsFrom(plan, counted);
  const anchor = anchorOf(counted, input.asOf);
  const f = forecast(plan, { asOf: input.asOf, ...checkInAnchors(plan, counted, input.asOf) });
  return {
    plan, checkpoints, checkpoint: anchor ? checkpoints.find((c) => c.id === anchor.id)! : null, forecast: f, today: parseDay(input.asOf), asOf: input.asOf, years: horizon,
    goals: f.goals,
  };
}

export const lastAmount = (series: Series): Decimal => series[series.length - 1]?.amount ?? core.ZERO;
